import { useTheme } from '../../hooks/useTheme'
import { Button } from './Button'
import { getSafeAreaPadding } from '../../utils/safeArea'

interface ErrorMessageProps {
  message: string
  title?: string
  icon?: string
  onRetry?: () => void
  retryLabel?: string
  fullScreen?: boolean
}

/**
 * Reusable error message component with optional retry button
 * 
 * @example
 * <ErrorMessage message="Failed to load data" onRetry={refetch} />
 * <ErrorMessage 
 *   title="Error" 
 *   message="Network error" 
 *   icon="⚠️"
 *   onRetry={refetch}
 *   retryLabel="Try Again"
 *   fullScreen 
 * />
 */
export function ErrorMessage({ 
  message, 
  title,
  icon = '❌',
  onRetry, 
  retryLabel = 'Try Again',
  fullScreen = false 
}: ErrorMessageProps) {
  const theme = useTheme()
  
  const content = (
    <div className="text-center py-10 px-5">
      <div className="text-6xl mb-4">{icon}</div>
      {title && (
        <h3 className="text-xl font-semibold mb-2">{title}</h3>
      )}
      <p className="mb-6 opacity-70">{message}</p>
      {onRetry && (
        <Button
          variant="primary"
          size="md"
          onClick={onRetry}
        >
          {retryLabel}
        </Button>
      )}
    </div>
  )

  if (fullScreen) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{
          backgroundColor: theme.bgColor,
          color: theme.textColor,
          ...getSafeAreaPadding(),
        }}
      >
        {content}
      </div>
    )
  }

  return content
}
